import { Component, Inject, ViewChild } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { Sort } from '@angular/material/sort';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { BehaviorSubject } from 'rxjs';
import { tap, first } from 'rxjs/operators';
import { indicate } from 'src/app/operators';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import { EsraPipingAPIClient, ServiceViewModel } from 'src/app/shared/models/autogenerated-piping';

export interface ServiceOption {
  id: string;
  name: string;
  description?: string;
  code?: string;
  selected?: boolean;
}

// Generic option interface that can be used for materials, services, etc.
export interface FieldOption {
  id: string;
  name: string;
  description?: string;
  code?: string;
  selected?: boolean;
}

export interface ServiceSelectionData {
  availableServices: ServiceOption[];
  selectedServices?: ServiceOption[]; // Services already selected
  singleSelection?: boolean; // Whether to allow only single selection
  title?: string; // Custom title for the modal
  fieldType?: string; // Type of field being selected ('service', 'material', etc.)
  context?: string; // Context: 'field-edit' or 'list-management'
}

// Generic data interface for field selection modals
export interface FieldSelectionData {
  availableOptions: FieldOption[];
  selectedOptions?: FieldOption[]; // Options already selected
  singleSelection?: boolean; // Whether to allow only single selection
  title?: string; // Custom title for the modal
  fieldType?: string; // Type of field (service, material, etc.)
}

@Component({
  selector: 'app-service-selection-modal',
  templateUrl: './service-selection-modal.component.html',
  styleUrls: ['./service-selection-modal.component.scss'],
  standalone: false
})
export class ServiceSelectionModalComponent {
  faSearch = faSearch;
  displayedColumns: string[] = ['select', 'item_code', 'item_description'];
  dataSource = new MatTableDataSource<ServiceOption>([]);

  @ViewChild(MatPaginator, { static: true }) matPaginator!: MatPaginator;

  searchQuery = '';
  sortBy: string = 'item_code';
  sortDirection: 'asc' | 'desc' = 'asc';
  pageSize: number = 10;
  pageIndex: number = 0;
  totalRecords: number = 0;
  isSearching$ = new BehaviorSubject<boolean>(false);
  searchResults: ServiceOption[] = [];
  selectedServices: ServiceOption[] = [];

  constructor(
    public dialogRef: MatDialogRef<ServiceSelectionModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ServiceSelectionData,
    private esraPipingAPIClient: EsraPipingAPIClient
  ) {
    // Initialize search results with data or empty array
    this.searchResults = data?.availableServices || [];
    this.totalRecords = this.searchResults.length;

    // Initialize selected services with existing selections
    this.selectedServices = data?.selectedServices ? [...data.selectedServices] : [];

    this.updateSearchResults();
  }

  ngOnInit(): void {
    // Only load services from API if no data was provided (for backward compatibility)
    // When used for Material or other fields, data is already provided in constructor
    if (!this.data?.availableServices || this.data.availableServices.length === 0) {
      this.loadServices();
    }
  }

  loadServices(): void {
    const indicator = indicate(this.isSearching$);

    this.esraPipingAPIClient.getAllCurrentServices()
    .pipe(
      first(),
      tap((services: ServiceViewModel[]) => {
        // Map ServiceViewModel to ServiceOption
        const serviceOptions: ServiceOption[] = (services || []).map((service: ServiceViewModel) => ({
          id: service.id || '',
          name: service.name || '',
          description: service.name || '', // Use name as description since no separate description field
          code: service.name || ''
        }));

        this.searchResults = serviceOptions;
        this.totalRecords = serviceOptions.length;

        // Maintain existing selections by matching IDs
        this.syncSelectedServices();

        this.updateSearchResults();
      }),
      indicator
    )
    .subscribe({
      error: (error) => {
        // Use provided data as fallback
        if (this.data?.availableServices) {
          this.searchResults = this.data.availableServices;
          this.totalRecords = this.searchResults.length;
          this.syncSelectedServices();
          this.updateSearchResults();
        }
      }
    });
  }

  syncSelectedServices(): void {
    // DON'T filter out services - we want to keep all pre-selected services
    // even if they're not currently in the search results

    // Just ensure the selected flag is properly set
    this.selectedServices.forEach(selectedService => {
      selectedService.selected = true;
    });
  }  updateSearchResults(): void {
    let filteredResults = [...this.searchResults];

    // Apply search filter
    if (this.searchQuery && this.searchQuery.trim()) {
      const searchLower = this.searchQuery.toLowerCase().trim();
      filteredResults = this.searchResults.filter(service =>
        (service.name && service.name.toLowerCase().includes(searchLower)) ||
        (service.description && service.description.toLowerCase().includes(searchLower)) ||
        (service.code && service.code.toLowerCase().includes(searchLower))
      );
    }

    // Apply sorting
    filteredResults.sort((a, b) => {
      let aVal: string;
      let bVal: string;

      switch (this.sortBy) {
        case 'item_description':
          aVal = a.description || '';
          bVal = b.description || '';
          break;
        default: // item_code
          aVal = a.code || a.name || '';
          bVal = b.code || b.name || '';
          break;
      }

      const comparison = aVal.localeCompare(bVal);
      return this.sortDirection === 'asc' ? comparison : -comparison;
    });

    // Apply pagination locally
    const startIndex = this.pageIndex * this.pageSize;
    const endIndex = startIndex + this.pageSize;
    const paginatedResults = filteredResults.slice(startIndex, endIndex);

    // Update data source with paginated results
    this.dataSource.data = paginatedResults;
    this.totalRecords = filteredResults.length;
  }

  onSearchChange(event: any): void {
    this.searchQuery = event.target.value || '';
    this.pageIndex = 0; // Reset to first page
    this.updateSearchResults();
  }

  onPageChange(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.updateSearchResults();
  }

  sortData(sort: Sort): void {
    this.sortBy = sort.active || 'service_code';
    this.sortDirection = sort.direction as 'asc' | 'desc' || 'asc';
    this.pageIndex = 0; // Reset to first page
    this.updateSearchResults();
  }

  goToPage(page: number): void {
    this.pageIndex = page;
    this.updateSearchResults();
  }

  get hasSelections(): boolean {
    return this.selectedServices.length > 0;
  }

  get isSingleSelection(): boolean {
    return this.data?.singleSelection === true;
  }

  get modalTitle(): string {
    return this.data?.title || 'Select Items';
  }

  get searchPlaceholder(): string {
    const type = this.getItemType();
    return ` Search ${type}`;
  }

  get codeColumnHeader(): string {
    const type = this.getItemType();
    return `${type} Code`;
  }

  get descriptionColumnHeader(): string {
    const type = this.getItemType();
    
    // Special handling for services
    if (type === 'Service') {
      // If it's for list management (adding services to a list), use "Last Updated"
      if (this.data?.context === 'list-management' || (!this.data?.singleSelection && !this.data?.context)) {
        return 'Last Updated';
      }
      // If it's for field editing (single selection), use "Service Description"
      return 'Service Description';
    }
    
    return `${type} Description`;
  }

  get selectedItemsText(): string {
    const type = this.getItemType();
    return `Selected ${type}s`;
  }

  get tableCommentText(): string {
    const type = this.getItemType();
    return `${type}s table with pagination`;
  }

  private getItemType(): string {
    const title = this.data?.title || '';
    if (title.toLowerCase().includes('material')) return 'Material';
    if (title.toLowerCase().includes('service')) return 'Service';
    if (title.toLowerCase().includes('rating')) return 'Rating';
    if (title.toLowerCase().includes('design')) return 'Design Code';
    if (title.toLowerCase().includes('valve')) return 'Valve Trim';
    if (title.toLowerCase().includes('examination')) return 'Examination';
    return 'Item';
  }

  checkboxChanged(checked: boolean, service: ServiceOption): void {
    if (checked) {
      // If single selection mode, clear all previous selections
      if (this.data?.singleSelection) {
        this.selectedServices = [{ ...service, selected: true }];
        // Auto-close modal in single selection mode
        this.dialogRef.close(this.selectedServices);
        return;
      } else {
        // Check if service is already selected using the same logic as isChecked
        const alreadySelected = this.selectedServices.some(s =>
          s.id === service.id ||
          (s.name && service.name && s.name.trim().toLowerCase() === service.name.trim().toLowerCase())
        );

        if (!alreadySelected) {
          this.selectedServices.push({ ...service, selected: true });
        }
      }
    } else {
      // Remove service using the same logic
      this.selectedServices = this.selectedServices.filter(s =>
        s.id !== service.id &&
        !(s.name && service.name && s.name.trim().toLowerCase() === service.name.trim().toLowerCase())
      );
    }
  }

  isChecked(service: ServiceOption): boolean {
    // Check by ID first, then by name as fallback
    return this.selectedServices.some(s =>
      s.id === service.id ||
      (s.name && service.name && s.name.trim().toLowerCase() === service.name.trim().toLowerCase())
    );
  }

  cancel(): void {
    this.dialogRef.close();
  }

  save(): void {
    if (this.selectedServices.length > 0) {
      this.dialogRef.close(this.selectedServices);
    }
  }
}
